import React, { useRef, useState } from 'react';
import cn from "clsx";
import { css } from "linaria";
import get from 'lodash/get';

import AppLink from '../../AppLink';

import CloseIcon from "../../Icons/Close";

import { useAuthState } from "../../../contexts/Auth/context";

import { getCurrencySymbol } from '../../../utils/currency';
import fastpanel from "../../../utils/fastpanel";

const pluralizeCoins = (numberToCheck) => `Coin${ numberToCheck > 1 ? 's' : ''}`;

const freeEventTextFunc = () => 'This event is free.';
const paidEventLoggedOutFunc = ({ priceToSend }) =>
  `This event costs ${priceToSend.toLocaleString('en-US')} ${pluralizeCoins(priceToSend)}. New users get free coins!`;
const paidEventCostFunc = ({ priceToSend, userCoinBalance }) =>
  `This event costs ${priceToSend.toLocaleString('en-US')} ${pluralizeCoins(priceToSend)}. You have ${userCoinBalance.toLocaleString('en-US')} ${pluralizeCoins(priceToSend)}.`;
const paidEventSignUpFunc = ({ openSignUpModal }) => (
  <span
    className="text-gatsby cursor-pointer"
    onClick={() => openSignUpModal(true)}
  >
    Sign up to send for free.
  </span>
);

const counterRemovalStyles = css`
  &::-webkit-inner-spin-button {
    display: none;
  }
`;

const paidEventBuyPromptFunc = ({
  coinPackageQuantity,
  coinPackagePrice,
  coinPackagePriceSymbol,
  numPackagesToBuy,
}) => (
  <AppLink
    className="text-gatsby"
    href="https://www.paperlesspost.com/buy_coins"
    target="_blank"
    isExternal
  >
    {numPackagesToBuy > 1
      ? `Buy ${numPackagesToBuy} ${coinPackageQuantity.toLocaleString(
          'en-US',
        )} Coins packages for ${coinPackagePriceSymbol}${coinPackagePrice.toLocaleString(
          'en-US',
        )} each to send.`
      : `Buy a ${coinPackageQuantity.toLocaleString(
          'en-US',
        )} Coins package for ${coinPackagePriceSymbol}${coinPackagePrice.toLocaleString(
          'en-US',
        )} to send.`}
  </AppLink>
);

const calculateCoinPrice = props => {
  const {
    inviteQuantity,
    isFreeInvite,
    coinsPerRecipient,
    freeSendCap,
    freeSendCapPerRecipientCoinPrice,
  } = props;

  if (isFreeInvite) {
    // Only free flyers get the first 50 emails free...
    return (
      Math.max(inviteQuantity - freeSendCap, 0) *
      freeSendCapPerRecipientCoinPrice
    );
  } else {
    return coinsPerRecipient * inviteQuantity;
  }
};

const calculateCoinPropertiesValues = (
  inviteQuantity,
  isInputValid,
  isLoggedIn,
  userCoinBalance,
  isFreeInvite,
  coinsPerRecipient,
  freeSendCap,
  freeSendCapPerRecipientCoinPrice,
  coinPackages,
) => {
  let priceToSend = null,
    coinDifference = null,
    coinPackageToBuy = null,
    numPackagesToBuy = null;

  if (isInputValid) {
    priceToSend = calculateCoinPrice({
      inviteQuantity,
      isFreeInvite,
      coinsPerRecipient,
      freeSendCap,
      freeSendCapPerRecipientCoinPrice,
    });

    coinDifference = priceToSend - (isLoggedIn ? userCoinBalance : 10);

    if (coinDifference > 0) {
      for (let i = 0; i < coinPackages.length; i++) {
        coinPackageToBuy = coinPackages[i];
        if (coinPackageToBuy.coins >= coinDifference) {
          break;
        }
      }
      numPackagesToBuy = Math.ceil(coinDifference / coinPackageToBuy.coins);
    }
  }

  return {
    inviteQuantity,
    priceToSend,
    coinDifference,
    coinPackageToBuy,
    numPackagesToBuy,
  };
};

const INPUT_MIN = 0;
const INPUT_MAX = 20000;

const CoinsCalculator = props => {
  const quantityInputEl = useRef(null);
  const { isLoggedIn, accountInfo: { id, coins } } = useAuthState();
  const userCoinBalance = coins;
  const {
    coinsPerRecipient,
    isFreeInvite,
    isInvite,
    coinPackages,
    freeSendCap,
    freeSendCapPerRecipientCoinPrice,
    preferredCurrency,
    openSignUpModal,
  } = props;
  const [submittedInputValue, setSubmittedInputValue] = useState(null);
  const quantityInput = quantityInputEl.current;
  const isInputValid = !quantityInput ? true : submittedInputValue !== null;

  const calculateSubmit = event => {
    event.preventDefault();
    event.stopPropagation();

    const quantityInput = quantityInputEl.current;
    const quantityInputValue = parseInt(quantityInput.value);
    // If this is an error, then it's invalid...
    let isInputValid = event.type === 'invalid' ? false : true;
    // If this still might be valid, then do the expensive validation check...
    if (isInputValid) {
      quantityInput.checkValidity();
    }
    quantityInput.setCustomValidity('');

    setSubmittedInputValue(isInputValid ? quantityInputValue : null);

    if (isInputValid) {
      const {
        inviteQuantity,
        coinDifference,
        coinPackageToBuy,
        numPackagesToBuy,
      } = calculateCoinPropertiesValues(
        quantityInputValue,
        isInputValid,
        isLoggedIn,
        userCoinBalance,
        isFreeInvite,
        coinsPerRecipient,
        freeSendCap,
        freeSendCapPerRecipientCoinPrice,
        coinPackages,
      );

      let package_recommended =
        coinDifference > 0 ? coinPackageToBuy.coins : null;
      package_recommended =
        numPackagesToBuy > 1 ? 'multiple' : package_recommended;

      // This implementation of tracking is intended to cover both click actions and hitting enter/return while the input is in focus.
      // This pattern NOT be used if using withClickTracking would otherwise suffice.
      fastpanel({
        name: "pbr_element_clicked",
        data: {
          id: "calculateCost",
          userID: isLoggedIn && id ? id : null,
          calculator_count: inviteQuantity,
          coin_balance: isLoggedIn ? userCoinBalance : null,
          package_recommended,
          checkout_required: isLoggedIn ? coinDifference > 0 : null,
        }
      });
    }
  };

  const {
    inviteQuantity,
    coinDifference,
    priceToSend,
    coinPackageToBuy,
    numPackagesToBuy,
  } = calculateCoinPropertiesValues(
    parseInt(submittedInputValue),
    isInputValid,
    isLoggedIn,
    userCoinBalance,
    isFreeInvite,
    coinsPerRecipient,
    freeSendCap,
    freeSendCapPerRecipientCoinPrice,
    coinPackages,
  );

  let line1Content = null;
  let line2Content = null;

  if (isInputValid && inviteQuantity !== null) {
    const coinsParameters = {
      priceToSend,
      userCoinBalance,
      coinPackageQuantity: get(coinPackageToBuy, 'coins', 0),
      coinPackagePrice:
        get(coinPackageToBuy, `prices[${preferredCurrency}]`, 0) / 100,
      coinPackagePriceSymbol: getCurrencySymbol(preferredCurrency),
      numPackagesToBuy,
      openSignUpModal,
    };

    if (coinDifference <= 0) {
      // If this is a free event with fewer invites than the free threshold...
      if (isFreeInvite && inviteQuantity <= freeSendCap) {
        line1Content = freeEventTextFunc(coinsParameters);
      }
      // If not logged in, show the sign up prompt...
      else if (!isLoggedIn) {
        line1Content = paidEventLoggedOutFunc(coinsParameters);
        line2Content = paidEventSignUpFunc(coinsParameters);
      }
      // Otherwise just show the cost breakdown...
      else {
        line1Content = paidEventCostFunc(coinsParameters);
      }
    } else {
      // If not logged in, show how many you get for free with the cost...
      if (!isLoggedIn) {
        line1Content = paidEventLoggedOutFunc(coinsParameters);
      }
      // Otherwise just show the cost breakdown...
      else {
        line1Content = paidEventCostFunc(coinsParameters);
      }
      // line 2 shows the suggested coin package...
      line2Content = paidEventBuyPromptFunc(coinsParameters);
    }
  } else if (!isInputValid) {
    line1Content = `Please use a number between ${INPUT_MIN} and ${INPUT_MAX.toLocaleString(
      'en-US',
    )}.`;
  }

  const messageType = isInvite ? 'invite' : 'card';
  const messageRecieverName = isInvite ? 'invite' : 'recipient';

  return (
    <div className="relative border border-lapel mt-4 mb-6">
      <div className="absolute top-0 text-center w-full pointer-events-none">
        <h3 className="heading-xsmall bg-white text-center px-1 w-fit max-w-[80%] mx-auto translate-y-[-50%]">
          How many {messageType}s would you like to send?
        </h3>
      </div>
      <div>
        <form
          className={
            cn(
              "flex flex-row items-center p-6 justify-center lg:flex-wrap",
              { 'flex-col': !isFreeInvite },
            )
          }
          onSubmit={calculateSubmit}
          onInvalid={calculateSubmit}
        >
          <span className="z-0 flex flex-row items-center">
            <input
              className={
                cn(
                  "text-center w-20 h-12 border border-wolf text-dusty-gray",
                  counterRemovalStyles,
                  {
                    'flex-auto': isFreeInvite,
                    'w-20': !isFreeInvite,
                    'text-lapel': inviteQuantity || !isInputValid,
                    'outline-error': !isInputValid,
                  }
                )
              }
              id="invite-quantity"
              ref={quantityInputEl}
              name="quantity"
              type="number"
              defaultValue="0"
              min={INPUT_MIN}
              max={INPUT_MAX}
              onFocus={() => (quantityInputEl.current.value = '')}
              required
            />
            <span className={cn("flex flex-row items-center", { 'hidden': isFreeInvite })}>
              <CloseIcon className="mr-2 ml-3" width=".75rem" height=".75rem" />
              <span>
                {coinsPerRecipient} Coins per {messageRecieverName}
              </span>
            </span>
          </span>
          <button
            className={
              cn(
                "flex-[0_0_auto] text-white bg-gatsby rounded-full normal-case tracking-normal heading-xsmall font-medium py-3 px-5 border-none cursor-pointer",
                {
                  "ml-4": isFreeInvite,
                  "mt-4": !isFreeInvite,
                }
              )
            }
            type="submit"
          >
            Calculate
          </button>
        </form>
        <div 
          className={
            cn(
              "text-center max-h-0 px-4 bg-panna-cotta overflow-hidden transition-[padding_max-height] duration-[250ms]",
              { "py-2 px-4 max-h-[7.5rem]": inviteQuantity || !isInputValid, "text-error bg-error-background": !isInputValid },
            )
          }
        >
          <div>{line1Content}</div>
          <div>{line2Content}</div>
        </div>
      </div>
    </div>
  );
};

export default CoinsCalculator;
